import { useCallback, useEffect, useState } from "react";
import { useMutation, useApolloClient } from "@apollo/client";
import { App, Form } from "antd";
import { CREATE_ACCOUNT, SEND_QUESTIONS } from "./createAccount.gql";
import useLocalStorage from "../../hooks/useLocalStorage";
import { MATCHING_DONE, QUESTIONS, SURVEY_DONE } from "../../constants/storage";
import { ApolloClientType } from "../../types/apollo";
import { DATE_FORMAT } from "../../constants/date";

const useCreateAccount = () => {
  const { removeStorageItem, getStorageJsonItem } = useLocalStorage();
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const { message } = App.useApp();
  const [form] = Form.useForm();
  const [isEmailConfirmation, setIsEmailConfirmation] = useState(false);
  const apolloClient = useApolloClient() as ApolloClientType;
  const formValues = Form.useWatch([], form);

  const [
    createAccount,
    { data: createAccountData, loading: createAccountLoading },
  ] = useMutation(CREATE_ACCOUNT, {
    fetchPolicy: "no-cache",
    onError({ graphQLErrors }) {
      message.error(graphQLErrors[0].message);
    },
  });

  const [sendQuestions] = useMutation(SEND_QUESTIONS, {
    fetchPolicy: "no-cache",
    onError({ graphQLErrors }) {
      message.error(graphQLErrors[0].message);
    },
  });

  useEffect(() => {
    form
      .validateFields({ validateOnly: true })
      .then(() => setSubmitDisabled(false))
      .catch(() => setSubmitDisabled(true));
  }, [form, formValues]);

  const onFinish = useCallback(
    async (values: any) => {
      const {
        name,
        surname,
        email,
        password,
        birthDate,
        birthPlace,
        phone,
        gender,
        address,
        cap,
        city,
        province,
        codFisc,
      } = values;

      const user = await createAccount({
        variables: {
          name,
          surname,
          email,
          password,
          birthPlace,
          phone: phone?.number ? phone.number : null,
          birthDate: birthDate.format(DATE_FORMAT.FULL_DATE),
          gender,
          address,
          cap,
          city,
          province,
          codFisc,
        },
      });

      if (!user.data.createUserAppPatient?.id) return;

      const questions = await sendQuestions({
        variables: {
          id: +user.data.createUserAppPatient.id,
          answers: getStorageJsonItem(QUESTIONS),
        },
      });

      if (questions.errors) return;

      await apolloClient.clearCacheData(apolloClient);
      setIsEmailConfirmation(true);
      form.resetFields();
      removeStorageItem(SURVEY_DONE);
      removeStorageItem(MATCHING_DONE);
      removeStorageItem(QUESTIONS);
    },
    [form, createAccountData]
  );

  return {
    form,
    submitDisabled,
    onFinish,
    passwordVisible,
    setPasswordVisible,
    createAccountLoading,
    isEmailConfirmation,
  };
};

export default useCreateAccount;
